/**
 * VPE Dashboard Refactored Component
 * Refactored version of VPEDashboard.tsx using Component + Hook + Service pattern
 * 
 * REFACTORING SUMMARY:
 * - Original: 937 lines of mixed concerns
 * - Refactored: 6 focused components + 2 hooks + 1 service
 * - Separation: UI components, state management, business logic, API calls
 * - Reusability: Each component can be used independently
 * - Testability: Each piece can be tested in isolation
 */

import React from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useBackButtonLogout } from '@/hooks/ea/useBackButtonLogout.ts';
import LogoutConfirmDialog from '@/components/common/LogoutConfirmDialog.tsx';
import EmailDialog from '@/components/features/communication/EmailDialog';
import { useVPEDashboardState } from '@/hooks/ea/useEADashboardState';
import { useVPEDashboardOperations } from '@/hooks/ea/useEADashboardOperations';
import { vpeDashboardService } from '@/services/ea/vpeDashboardService';
import DashboardHeader from '@/components/ea/DashboardHeader';
import ActionCardsSection from '@/components/ea/ActionCardsSection';
import DashboardStatsSection from '@/components/ea/DashboardStatsSection';
import SearchFilterSection from '@/components/ea/SearchFilterSection';
import GroupsTableSection from '@/components/ea/GroupsTableSection';

const VPEDashboardRefactored = () => {
  
  // State Management Hook
  const { state, actions } = useVPEDashboardState();

  // Operations Hook
  const operations = useVPEDashboardOperations(state, actions);

  // Handle browser back button to show logout dialog
  useBackButtonLogout({
    onLogoutRequest: () => actions.setShowLogoutDialog(true)
  });

  // Calculate dashboard stats
  const stats = vpeDashboardService.calculateStats(operations.groups);

  // Calculate pagination data
  const paginationData = vpeDashboardService.paginateGroups(
    state.filteredGroups,
    state.currentPage,
    state.rowsPerPage
  );

  // Note: All EA operations now use dedicated routes instead of view-based navigation:
  // - Group operations: /group-operations/:operation/:id?
  // - Archived groups: /archived-groups
  // - Global search: /global-search
  // - Manage ES: /manage-es

   return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="ea-dashboard">
      {/* Main Content */}
      <main className="p-8 max-w-7xl mx-auto" data-testid="ea-dashboard-content">
        {/* Action Cards */}
        <ActionCardsSection
          onCreateGroupClick={operations.handleCreateGroupClick}
          onArchivedGroupsClick={operations.handleArchivedGroupsClick}
          onManageVPEClick={operations.handleManageVPEClick}
          data-testid="ea-dashboard-action-cards"
        />

        {/* Dashboard Stats */}
        <DashboardStatsSection 
          stats={stats} 
          data-testid="ea-dashboard-stats"
        />

        {/* Search and Filter */}
        <SearchFilterSection
          searchBy={state.searchBy}
          searchTerm={state.searchTerm}
          onSearchByChange={actions.setSearchBy}
          onSearchTermChange={actions.setSearchTerm}
          onSearch={operations.handleSearch}
          onClearSearch={operations.handleClearSearch}
          data-testid="ea-dashboard-search-filter"
        />

        {/* Groups Table */}
        <GroupsTableSection
          paginatedGroups={paginationData.paginatedGroups}
          isLoading={operations.isLoading}
          archiveButtonLoading={state.archiveButtonLoading}
          currentPage={state.currentPage}
          totalPages={paginationData.totalPages}
          rowsPerPage={state.rowsPerPage}
          startIndex={paginationData.startIndex}
          totalGroups={paginationData.totalItems}
          onEmailClick={operations.handleEmailClick}
          onEditClick={operations.handleEditClick}
          onDuplicateClick={operations.handleDuplicateClick}
          onArchiveClick={operations.handleArchiveClick}
          onDeleteClick={operations.handleDeleteClick}
          onPageChange={(page) => actions.setCurrentPage(page)}
          onRecordsPerPageChange={(recordsPerPage) => {
            actions.setRowsPerPage(recordsPerPage);
            actions.setCurrentPage(1);
          }}
        />
      </main>

      {/* Email Dialog */}
      <EmailDialog
        open={state.showEmailDialog}
        onOpenChange={actions.setShowEmailDialog}
        groupId={state.selectedGroupId}
        onSuccess={operations.handleEmailSuccess}
      />

       {/* Archive Confirmation Dialog */}
      <AlertDialog open={state.showArchiveDialog} onOpenChange={actions.setShowArchiveDialog}>
        <AlertDialogContent data-testid="ea-archive-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive this group? This action can be undone later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ea-archive-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={operations.handleArchiveGroup}
              disabled={state.isArchiveLoading}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="ea-archive-confirm-button"
            >
              {state.isArchiveLoading ? "Archiving..." : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={state.showDeleteDialog} onOpenChange={actions.setShowDeleteDialog}>
        <AlertDialogContent data-testid="ea-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this group? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ea-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={operations.handleDeleteGroup}
              disabled={state.isDeleteLoading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="ea-delete-confirm-button"
            >
              {state.isDeleteLoading ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Logout Confirmation Dialog */}
      <LogoutConfirmDialog
        open={state.showLogoutDialog}
        onOpenChange={actions.setShowLogoutDialog}
        onConfirm={operations.handleLogout}
      />
    </div>
  );
};

export default VPEDashboardRefactored;